import { definePreset } from '@primeuix/themes';
import LaraCompat from '@primeuix/themes/lara-compat';

// This preset only lists genuine customizations on top of PrimeNG's Lara preset (compat variant,
// which preserves this app's pre-existing component padding/spacing after PrimeNG v22's font-size
// baseline shift). The original file was a ~5,400-line full expansion of the Lara preset with the
// actual overrides mixed in; it was regenerated by diffing against Lara's stock defaults to isolate
// exactly what differs (primary green/info-blue palettes and a custom neutral surface palette) —
// everything else was Lara's own defaults, redundantly copied. Two additional entries found in the
// old file (`components.menu.submenuLabel.background` and
// `components.megamenu.submenuLabel.background`, both referencing the malformed token
// `{navigation.submenu.label.background.}` — note the trailing period) were dropped as clear
// copy-paste typos rather than intentional overrides; `p-menu`/`p-megamenu` aren't used anywhere in
// this repo, so this has zero behavioral effect either way.
//
// semantic.typography.fontSize override: this repo's apps set `body { font-size: 14px; }` globally
// (see each app's styles.css), so pre-v22 text rendered at 14px via plain CSS inheritance — the old
// Lara version never defined its own font-size token, so nothing overrode that inherited value. v22
// introduced an explicit `typography.fontSize` token that many components (p-table body cells,
// p-chip, p-textarea, p-fileupload, p-datepicker, etc.) now read directly instead of inheriting;
// lara-compat sets it to 1rem (=16px against this app's real 16px document root, since only `body`
// is overridden, not `html`), which is measurably larger than the 14px everything rendered at
// before. Setting it to 0.875rem (=14px at that same 16px root) restores the original size across
// every component that reads this token. Table headers were never affected by this bug since they
// have no such token and simply kept inheriting `body`'s 14px the whole time.
export const MyPreset = definePreset(LaraCompat, {
  primitive: {
    green: {
      50: '#f3f9f2',
      100: '#c7e3c2',
      200: '#9acc92',
      300: '#6eb662',
      400: '#419f31',
      500: '#158901',
      600: '#127401',
      700: '#0f6001',
      800: '#0c4b01',
      900: '#083700',
      950: '#052200',
    },
    blue: {
      50: '#f2f7f9',
      100: '#c2d7e3',
      200: '#92b8cc',
      300: '#6298b6',
      400: '#31799f',
      500: '#015989',
      600: '#014c74',
      700: '#013e60',
      800: '#01314b',
      900: '#002437',
      950: '#001622',
    },
  },
  semantic: {
    typography: {
      fontSize: '0.875rem',
    },
    primary: {
      50: '{green.50}',
      100: '{green.100}',
      200: '{green.200}',
      300: '{green.300}',
      400: '{green.400}',
      500: '{green.500}',
      600: '{green.600}',
      700: '{green.700}',
      800: '{green.800}',
      900: '{green.900}',
      950: '{green.950}',
    },
    // Custom neutral surface palette. Dark-mode is permanently disabled in this app (see
    // darkModeSelector: '.fake-dark-mode' in app.config.ts), so the dark half of each light-dark()
    // pair below is left as Lara's own zinc-based default rather than a bespoke value, since it can
    // never actually render.
    surface: {
      50: 'light-dark(#f8fafc, {zinc.50})',
      100: 'light-dark(#f1f5f9, {zinc.100})',
      200: 'light-dark(#e2e8f0, {zinc.200})',
      300: 'light-dark(#cbd5e1, {zinc.300})',
      400: 'light-dark(#94a3b8, {zinc.400})',
      500: 'light-dark(#64748b, {zinc.500})',
      600: 'light-dark(#475569, {zinc.600})',
      700: 'light-dark(#334155, {zinc.700})',
      800: 'light-dark(#1e293b, {zinc.800})',
      900: 'light-dark(#0f172a, {zinc.900})',
      950: 'light-dark(#020617, {zinc.950})',
    },
  },
  components: {
    button: {
      root: {
        // "info" severity button remapped from Lara's default sky to this app's custom blue ramp
        info: {
          background: 'light-dark({blue.500}, {sky.400})',
          hoverBackground: 'light-dark({blue.600}, {sky.300})',
          activeBackground: 'light-dark({blue.700}, {sky.200})',
          borderColor: 'light-dark({blue.500}, {sky.400})',
          hoverBorderColor: 'light-dark({blue.600}, {sky.300})',
          activeBorderColor: 'light-dark({blue.700}, {sky.200})',
          focusRing: {
            shadow: 'light-dark(0 0 0 0.2rem {blue.200}, 0 0 0 0.2rem color-mix(in srgb, {sky.400}, transparent 80%))',
          },
        },
      },
    },
  },
});
